import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard,
  Users,
  ListChecks,
  Bell,
  CalendarDays,
  BarChart3,
  LogOut,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true, testId: "sidebar-dashboard" },
  { to: "/app/directory", label: "Directory", icon: Users, testId: "sidebar-directory" },
  { to: "/app/tasks", label: "Tasks", icon: ListChecks, testId: "sidebar-tasks" },
  { to: "/app/notifications", label: "Notifications", icon: Bell, testId: "sidebar-notifications" },
  { to: "/app/leaves", label: "Leaves", icon: CalendarDays, testId: "sidebar-leaves" },
  { to: "/app/reports", label: "Reports", icon: BarChart3, testId: "sidebar-reports" },
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const initials = (user?.name || "?")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("");

  return (
    <div className="min-h-screen bg-[#f7f7f5] text-[#1c1c19]">
      {/* Sidebar */}
      <aside
        className="fixed inset-y-0 left-0 hidden w-[260px] flex-col bg-[#1c1c19] text-[#f7f7f5] md:flex"
        data-testid="app-sidebar"
      >
        <div className="px-6 pt-8 pb-10">
          <div className="overline text-[#8a8a80]">Workspace</div>
          <div className="mt-1 font-display text-xl font-bold tracking-tight">
            Shreya · Analyst
          </div>
        </div>
        <nav className="flex-1 px-3">
          {NAV.map(({ to, label, icon: Icon, end, testId }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testId}
              className={({ isActive }) =>
                `mb-1 flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors duration-200 ease-in-out ${
                  isActive
                    ? "bg-[#2a2a26] text-[#f7f7f5]"
                    : "text-[#a8a89e] hover:bg-[#2a2a26] hover:text-[#f7f7f5]"
                }`
              }
            >
              <Icon strokeWidth={1.5} className="h-4 w-4" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-[#2a2a26] p-4">
          <div className="flex items-center gap-3">
            <Avatar className="h-9 w-9">
              <AvatarFallback className="bg-[#4a6b53] text-[#f7f7f5]">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="truncate text-sm font-medium">{user?.name}</div>
              <div className="overline mt-0.5 text-[#8a8a80]">
                {user?.role === "admin" ? "HR Admin" : "Employee"}
              </div>
            </div>
            <Button
              data-testid="sidebar-logout-btn"
              onClick={doLogout}
              variant="ghost"
              size="icon"
              className="text-[#a8a89e] hover:bg-[#2a2a26] hover:text-[#f7f7f5]"
              aria-label="Logout"
            >
              <LogOut strokeWidth={1.5} className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="md:pl-[260px]">
        {/* Mobile topbar */}
        <div className="flex items-center justify-between border-b border-[#e5e5e0] bg-white/80 px-4 py-3 backdrop-blur-md md:hidden">
          <div className="font-display text-lg font-bold">Shreya · Analyst</div>
          <Button
            variant="ghost"
            size="sm"
            onClick={doLogout}
            data-testid="mobile-logout-btn"
          >
            <LogOut strokeWidth={1.5} className="h-4 w-4" />
          </Button>
        </div>

        {/* Mobile bottom nav */}
        <nav className="fixed bottom-0 left-0 right-0 z-30 flex justify-around border-t border-[#e5e5e0] bg-white/95 py-2 md:hidden">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 px-2 py-1 text-[10px] ${
                  isActive ? "text-[#4a6b53]" : "text-[#575752]"
                }`
              }
            >
              <Icon strokeWidth={1.5} className="h-4 w-4" />
              {label}
            </NavLink>
          ))}
        </nav>

        <main className="mx-auto max-w-[1240px] px-4 pb-24 pt-6 md:px-10 md:pb-10 md:pt-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
